import React, { useEffect, useState } from 'react';
import { Offcanvas, Table } from 'react-bootstrap';
import ReactPaginate from 'react-paginate';
import ListSearchBar from './ListSearchBar';
import { useTranslate } from '../hooks/LanguageProvider';

export interface Subaccount {
  account_type: "admin" | "global" | "subaccount";
  has_subaccounts?: number;
  id_role: number;
  company: string;
  email_user: string;
  phone_user: string;
  wilaya: string;
  date_creation_user: string;
  id_user: number;
  username_user: string;
  nom_user: string;
  prenom_user: string;
}

export function subaccountSearchValue(account: Subaccount, criterion: string): string {
  if (criterion === 'full_name') return [account.nom_user, account.prenom_user].filter(Boolean).join(' ').trim();
  if (criterion === 'id_user') return String(account.id_user);
  if (criterion === 'phone_user') return String(account.phone_user ?? '');
  if (criterion === 'wilaya') return account.wilaya || '';
  return account.username_user || '';
}

export default function UserSubaccounts({ parentId, parentName, renderActions }: { parentId: number; parentName: string; renderActions: (account: Subaccount) => React.ReactNode }) {
  const { translate } = useTranslate();
  const [open, setOpen] = useState(false);
  const [trail, setTrail] = useState([{id: parentId, name: parentName}]);
  const current = trail[trail.length - 1];
  const [search, setSearch] = useState('');
  const [criterion, setCriterion] = useState('username_user');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [accounts, setAccounts] = useState<Subaccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    setSearch('');
    setPage(1);
    setLoading(true);
    setError(false);
    setAccounts([]);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/users/${current.id}/subaccounts`, {
      credentials: 'include', signal: controller.signal,
    }).then(async response => {
      if (!response.ok) throw new Error('Unable to load subaccounts');
      const rows = await response.json();
      if (!Array.isArray(rows)) throw new Error('Invalid subaccounts');
      if (!controller.signal.aborted) setAccounts(rows);
    }).catch(() => {
      if (!controller.signal.aborted) setError(true);
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [open, current.id]);

  const term = search.trim().toLocaleLowerCase();
  const visibleAccounts = accounts.filter(account => {
    const values = criterion === 'full_name'
      ? [`${account.nom_user || ''} ${account.prenom_user || ''}`, `${account.prenom_user || ''} ${account.nom_user || ''}`]
      : [account[criterion as 'username_user' | 'phone_user' | 'wilaya']];
    return values.some(value => String(value || '').toLocaleLowerCase().includes(term));
  });
  const pageCount = Math.max(1, Math.ceil(visibleAccounts.length / limit));
  const pageAccounts = visibleAccounts.slice((page - 1) * limit, page * limit);

  return <div className="users-subaccounts">
    <button type="button" className="users-icon-action users-subaccounts-toggle mr-2" aria-expanded={open}
      title={translate('Subaccounts')} aria-label={translate('Subaccounts')}
      aria-controls={`subaccounts-${parentId}`} onClick={() => {setTrail([{id: parentId, name: parentName}]); setOpen(true);}}>
      <i className="fas fa-sitemap" aria-hidden="true" />
    </button>
    <Offcanvas show={open} onHide={() => setOpen(false)} placement="end"
      id={`subaccounts-${parentId}`} className="users-subaccounts-drawer"
      aria-labelledby={`subaccounts-title-${parentId}`}>
      <Offcanvas.Header closeButton closeLabel={translate('Close')}>
        <Offcanvas.Title id={`subaccounts-title-${parentId}`}>
          <i className="fas fa-sitemap mr-2" aria-hidden="true" />
          {translate('Subaccounts')}
          <small className="d-block mt-1">{current.name}</small>
          {trail.length > 1 && <button type="button" className="btn btn-sm btn-outline-secondary mt-2"
            onClick={() => setTrail(previous => previous.slice(0, -1))}>
            <i className="fas fa-arrow-left mr-2" aria-hidden="true" />{trail[trail.length - 2].name}
          </button>}
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body aria-busy={loading}>
        <div className="users-subaccounts-toolbar"><ListSearchBar criteria={<select className="form-select" value={criterion}
          aria-label={translate('Search users')} onChange={event => {setCriterion(event.target.value); setPage(1);}}>
          <option value="username_user">{translate('User name')}</option>
          <option value="full_name">{translate('Full name')}</option>
          <option value="phone_user">{translate('Phone')}</option>
          <option value="wilaya">{translate('region')}</option>
        </select>} onReset={() => {setSearch(''); setPage(1);}} resetLabel={translate('Reset search')}>
          <input type="search" className="form-control" value={search}
            onChange={event => {setSearch(event.target.value); setPage(1);}}
            aria-label={translate('Search users')} placeholder={translate('Search users')} />
        </ListSearchBar></div>
        <div className="users-subaccounts-content">{loading ? <span role="status">{translate('Loading subaccounts')}</span>
          : error ? <span role="alert">{translate('Unable to load subaccounts')}</span>
          : <>
            <p className="mt-3">{translate('Displaying')} {pageAccounts.length} {translate('out of')} {visibleAccounts.length}</p>
            {accounts.length === 0 ? <span>{translate('No subaccounts')}</span>
              : visibleAccounts.length === 0 ? <span>{translate('No user found')}</span>
              : <div className="users-table-wrap"><Table className="users-table">
                <thead className="bg-white text-uppercase"><tr className="ligth ligth-data">
                  {['Id', 'name', 'User name', 'Account type', 'Company', 'Email', 'Phone', 'region', 'Registration date', 'Actions'].map(key => <th key={key}>{translate(key)}</th>)}
                </tr></thead>
                <tbody className="ligth-body">{pageAccounts.map(account => <tr key={account.id_user}>
                  <td>{account.id_user}</td>
                  <td><div className="users-name-cell"><span className="users-avatar">{(account.nom_user || account.username_user || '?').charAt(0).toUpperCase()}</span><span>{account.nom_user} {account.prenom_user}</span></div></td>
                  <td>{account.username_user}</td>
                  <td><span className="users-account-badge users-account-subaccount"><i className="fas fa-user" aria-hidden="true" />{translate('Subaccount')}</span></td>
                  <td>{account.company}</td><td>{account.email_user}</td><td>{account.phone_user}</td><td>{account.wilaya}</td>
                  <td>{account.date_creation_user ? new Date(account.date_creation_user).toLocaleDateString() : '—'}</td>
                  <td><div className="d-flex align-items-center list-action users-row-actions">
                    {Number(account.has_subaccounts) === 1 && <button type="button"
                      className="users-icon-action users-subaccounts-toggle mr-2"
                      title={translate('Subaccounts')} aria-label={`${translate('Subaccounts')} : ${account.username_user}`}
                      onClick={() => setTrail(previous => {
                        const existing = previous.findIndex(item => item.id === account.id_user);
                        return existing >= 0 ? previous.slice(0, existing + 1) : [...previous, {id: account.id_user, name: account.username_user}];
                      })}><i className="fas fa-sitemap" aria-hidden="true" /></button>}
                    <div className="d-flex align-items-center" onClickCapture={() => setOpen(false)}>{renderActions(account)}</div>
                  </div></td>
                </tr>)}</tbody>
              </Table></div>}
          </>}</div>
            <div className="users-subaccounts-pagination d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3">
              <select aria-label={translate('Displaying')} className="form-select w-auto" value={limit} onChange={event => {setLimit(Number(event.target.value));setPage(1);}}>
                {[10, 25, 50, 100].map(size => <option key={size} value={size}>{size}</option>)}
              </select>
              <ReactPaginate
                pageCount={pageCount} forcePage={page - 1}
                onPageChange={({selected}) => setPage(selected + 1)}
                previousLabel={translate('previous')} nextLabel={translate('next')}
                breakLabel="…" marginPagesDisplayed={2} pageRangeDisplayed={3}
                containerClassName="pagination mb-0" pageClassName="page-item" pageLinkClassName="page-link"
                previousClassName="page-item" previousLinkClassName="page-link"
                nextClassName="page-item" nextLinkClassName="page-link"
                breakClassName="page-item" breakLinkClassName="page-link" activeClassName="active"
              />
            </div>
      </Offcanvas.Body>
    </Offcanvas>
  </div>;
}
